import React, { forwardRef, useContext } from "react";
import PropTypes from "prop-types";

import { ThemeContext } from "../Theme";
import { ListProvider } from "./ListContext";
import { Icon } from "../Icon";

import styles from "./List.scss";
import ListItem from "./ListItem";
import ListText from "./ListText";

export const List = forwardRef(
  ({ children, icon, variants, customClass, ...tagProperties }, ref) => {
    const context = useContext(ThemeContext);

    let classNames = variants;

    if (!classNames) classNames = styles.list;

    if (typeof classNames !== "string") classNames = classNames.join(" ");

    const CustomTag =
      classNames.indexOf(List.Variant.ordered) >= 0 ? "ol" : "ul";

    const iconSizeStyles =
      icon?.size ||
      (classNames.indexOf(List.Variant.additional) >= 0
        ? Icon.Size.xsmall
        : Icon.Size.small);

    if (context) classNames += ` ${styles[context]}`;

    if (customClass) classNames += ` ${customClass}`;

    classNames = `${icon ? styles.withIcons : styles.list} ${classNames}`;

    return (
      <CustomTag className={classNames} ref={ref} {...tagProperties}>
        <ListProvider value={{ icon, iconSizeStyles }}>{children}</ListProvider>
      </CustomTag>
    );
  }
);

List.propTypes = {
  variants: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  customClass: PropTypes.string,
  icon: PropTypes.shape({
    icon: PropTypes.elementType,
    color: PropTypes.string,
    size: PropTypes.string,
    wrapperProps: PropTypes.objectOf(PropTypes.any),
  }),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

List.defaultProps = {
  variants: "",
  icon: null,
  customClass: null,
};

List.Item = ListItem;

List.Text = ListText;

List.Variant = {
  disc: styles.disc,
  noBullets: styles.noBullets,
  ordered: styles.ordered,
  additional: styles.additional,
  dense: styles.dense,
  responsive: styles.responsive,
  // when using individual icons per list item
  // otherwise automatically assigned via context when providing an
  // icon on the top level component
  withIcons: styles.withIcons,
};

List.contextType = ThemeContext;

List.displayName = "List";

export default List;
